import { useState, useEffect } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import type { LegalSection, ActiveAgreement } from '@mondivago/types';

export interface UseAgreementModalOptions {
  documentType: string;
  logTable: 'supplier_agreement_logs' | 'partner_agreement_logs';
  idField: 'supplier_id' | 'partner_id';
  profileTable: 'suppliers' | 'partners';
  rpcName: 'insert_supplier_agreement_log' | 'insert_partner_agreement_log';
  seenKey: string;
}

export interface UseAgreementModalReturn {
  showModal: boolean;
  profileId: string | null;
  activeAgreement: ActiveAgreement | null;
  hasNewVersion: boolean;
  acceptedVersion: string | null;
  loading: boolean;
  handleClose: () => void;
  openModal: () => void;
  refresh: () => void;
  acceptAgreement: (userId: string, activeAgreement: ActiveAgreement, ipAddress: string) => Promise<string | null>;
}

export function useAgreementModal({
  documentType,
  logTable,
  idField,
  profileTable,
  rpcName,
  seenKey,
}: UseAgreementModalOptions): UseAgreementModalReturn {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [activeAgreement, setActiveAgreement] = useState<ActiveAgreement | null>(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const checkAgreement = async () => {
      try {
        // 1. Resolve profile ID (supplier or partner)
        const { data: profile } = await supabase.from(profileTable).select('id').eq('user_id', user.id).maybeSingle();

        if (cancelled) return;

        const pid = profile?.id ?? null;
        setProfileId(pid);

        // 2. Fetch active legal document
        const { data: activeDoc } = await supabase
          .from('legal_documents')
          .select('id, version, title, document_hash, content')
          .eq('document_type', documentType)
          .eq('is_active', true)
          .maybeSingle();

        if (cancelled) return;

        if (activeDoc) {
          const rawContent = typeof activeDoc.content === 'string' ? activeDoc.content : '';
          const blocks = rawContent.split(/\n(?=##\s)/).filter(Boolean);
          const sections: LegalSection[] = blocks.map((block, i) => {
            const lines = block.trim().split('\n');
            const titleLine = lines.find((l) => l.startsWith('## '));
            const title = titleLine ? titleLine.replace(/^##\s+/, '').trim() : `Section ${i + 1}`;
            const paragraphs = lines
              .filter((l) => !l.startsWith('## '))
              .map((l) => l.replace(/^\s*[-*]\s*/, ''))
              .filter(Boolean);
            return { id: `section-${i + 1}`, title, paragraphs };
          });

          setActiveAgreement({
            id: activeDoc.id,
            version: activeDoc.version,
            title: activeDoc.title,
            document_hash: activeDoc.document_hash,
            sections,
          });
        }

        if (!pid) {
          setLoading(false);
          return;
        }

        // 3. Check the LATEST accepted version in agreement logs
        const { data: versionLog } = await supabase
          .from(logTable)
          .select('agreement_version')
          .eq(idField, pid)
          .order('accepted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled) return;

        const lastAcceptedVersion = versionLog?.agreement_version ?? null;
        setAcceptedVersion(lastAcceptedVersion);

        // 4. Compare versions
        if (activeDoc && lastAcceptedVersion !== activeDoc.version) {
          setHasNewVersion(true);
          setShowModal(true);
        } else {
          setHasNewVersion(false);
          setShowModal(false);
        }
      } catch {
        const seen = localStorage.getItem(seenKey);
        if (seen !== 'true' && !cancelled) {
          setTimeout(() => {
            setShowModal(true);
          }, 800);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void checkAgreement();

    return () => {
      cancelled = true;
    };
  }, [user?.id, refreshKey, documentType, logTable, idField, profileTable, seenKey]);

  const handleClose = () => {
    setShowModal(false);
  };

  const openModal = () => {
    localStorage.removeItem(seenKey);
    setShowModal(true);
  };

  const refresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const acceptAgreement = async (
    userId: string,
    agreement: ActiveAgreement,
    ipAddress: string
  ): Promise<string | null> => {
    if (!profileId) return 'Profile not found.';
    const rpcParams =
      idField === 'supplier_id'
        ? {
            p_supplier_id: profileId,
            p_user_id: userId,
            p_agreement_version: agreement.version,
            p_document_hash: agreement.document_hash,
            p_ip_address: ipAddress,
            p_user_agent: navigator.userAgent,
          }
        : {
            p_partner_id: profileId,
            p_user_id: userId,
            p_agreement_version: agreement.version,
            p_document_hash: agreement.document_hash,
            p_ip_address: ipAddress,
            p_user_agent: navigator.userAgent,
          };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: dbError } = await supabase.rpc(rpcName as any, rpcParams as any);
    return dbError ? dbError.message : null;
  };

  return {
    showModal,
    profileId,
    activeAgreement,
    hasNewVersion,
    acceptedVersion,
    loading,
    handleClose,
    openModal,
    refresh,
    acceptAgreement,
  };
}
